import { LIVE_STALE_MS, type LiveMember, type LiveSetup, type LiveSnapshot, type LiveState, type LiveTracked, type Scope } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api } from "./api.ts";

export interface LiveFeed {
  me: string | null;
  members: LiveMember[];
  /** Each member's game, by user id. */
  states: Record<string, LiveState>;
  /** The server's time now, kept ticking every second: how old an update is, whatever this device's clock says. */
  now: number;
  /** True while the stream to the server is open. */
  connected: boolean;
}

/** The server pings every 15 s: this long without a word, the stream is dead even if the browser has not noticed. */
export const STREAM_SILENT_MS = 40_000;
/** Waits between attempts to reopen a stream the browser gave up on: 1 s, 2 s, 4 s… up to this. */
const RETRY_MAX_MS = 15_000;
/** A stream down this long shows as reconnecting: a quick reopen goes unnoticed. */
const DOWN_SHOWN_AFTER_MS = 5_000;

/**
 * The team's games, live: a snapshot, then every change, over server-sent events.
 *
 * The browser reopens a dropped stream by itself, but not always: an error answer
 * (the server restarting after a deploy, a proxy's 502) closes it for good, and a
 * laptop waking up or a proxy dropping the line without a word can leave it open and
 * silent. So the page reopens it itself: when it is closed, when no ping came for
 * 40 s, and at once when the page is shown again or the network comes back. Each
 * opening starts with a fresh snapshot.
 */
export function useLiveFeed(scope: Scope): LiveFeed {
  const [me, setMe] = useState<string | null>(null);
  const [members, setMembers] = useState<LiveMember[]>([]);
  const [states, setStates] = useState<Record<string, LiveState>>({});
  const [offset, setOffset] = useState(0);
  const [connected, setConnected] = useState(false);
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    if (typeof EventSource === "undefined") return;
    let source: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let heard = Date.now();
    let stopped = false;
    // Since when the stream has been down, or null while it works.
    let downAt: number | null = null;
    const down = () => {
      downAt ??= Date.now();
    };

    const open = () => {
      if (stopped) return;
      clearTimeout(retry);
      source?.close();
      heard = Date.now();
      const s = new EventSource(`/api/live/stream?scope=${encodeURIComponent(scope)}`, { withCredentials: true });
      source = s;
      const alive = () => {
        heard = Date.now();
        attempts = 0;
        downAt = null;
        setConnected(true);
      };
      s.addEventListener("snapshot", (e) => {
        alive();
        const snap = JSON.parse((e as MessageEvent<string>).data) as LiveSnapshot;
        setMe(snap.me);
        setMembers(snap.members);
        setStates(Object.fromEntries(snap.states.map((x) => [x.userId, x])));
        setOffset(Date.parse(snap.now) - Date.now());
      });
      s.addEventListener("state", (e) => {
        alive();
        const x = JSON.parse((e as MessageEvent<string>).data) as LiveState;
        setStates((prev) => ({ ...prev, [x.userId]: x }));
      });
      s.addEventListener("ping", (e) => {
        alive();
        const now = Date.parse((JSON.parse((e as MessageEvent<string>).data) as { now: string }).now);
        if (Number.isFinite(now)) setOffset(now - Date.now());
      });
      s.onerror = () => {
        if (source !== s) return;
        down();
        // Still CONNECTING: the browser retries by itself. CLOSED: it gave up, so we try again.
        if (s.readyState === EventSource.CLOSED) later();
      };
    };
    const later = () => {
      clearTimeout(retry);
      retry = setTimeout(open, Math.min(RETRY_MAX_MS, 1000 * 2 ** attempts));
      attempts++;
    };
    const dead = () => !source || source.readyState === EventSource.CLOSED || Date.now() - heard > STREAM_SILENT_MS;
    const watchdog = setInterval(() => {
      if (downAt !== null && Date.now() - downAt > DOWN_SHOWN_AFTER_MS) setConnected(false);
      if (!dead()) return;
      down();
      // Already waiting to try again: let that wait run.
      if (source?.readyState !== EventSource.CLOSED) open();
    }, 1000);
    const back = () => {
      if (document.visibilityState === "visible" && dead()) open();
    };
    document.addEventListener("visibilitychange", back);
    window.addEventListener("online", back);
    open();
    return () => {
      stopped = true;
      clearTimeout(retry);
      clearInterval(watchdog);
      document.removeEventListener("visibilitychange", back);
      window.removeEventListener("online", back);
      source?.close();
    };
  }, [scope]);

  useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  return { me, members, states, now: tick + offset, connected };
}

/** True when the game spoke recently: in a match it sends a heartbeat every 10 s. */
export const isFresh = (s: LiveState | undefined, now: number) => !!s && now - Date.parse(s.updatedAt) < LIVE_STALE_MS;

/** The player's Live file and when the game last used it; polled while `poll` (the setup page waits for the game). */
export function useLiveSetup(poll = true) {
  return useQuery({ queryKey: ["live", "setup"], queryFn: () => api<LiveSetup>("GET", "/live/setup"), refetchInterval: poll ? 15_000 : false });
}

export function useNewLiveToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<LiveSetup>("POST", "/live/token"),
    onSuccess: (data) => qc.setQueryData(["live", "setup"], data),
  });
}

/** Who tracks which stuff on a map. Under "stuffs": a status change refreshes it. */
export function useTracked(scope: Scope, map: string | null) {
  return useQuery({
    queryKey: ["stuffs", "live-tracked", scope, map],
    queryFn: () => api<LiveTracked[]>("GET", `/live/tracked?scope=${encodeURIComponent(scope)}&map=${map}`),
    enabled: !!map,
  });
}

/** stuffId → user ids tracking it (any status). */
export function useTrackers(tracked: LiveTracked[] | undefined) {
  return useMemo(() => {
    const out = new Map<string, string[]>();
    for (const t of tracked ?? []) out.set(t.stuffId, [...(out.get(t.stuffId) ?? []), t.userId]);
    return out;
  }, [tracked]);
}
